import Modal from "../../../components/Atomos/Modals/Modal";
import Button from "../../../components/staff/Button";

export type PaymentReminderIssue = {
  name: string;
  reason: string;
  kind: "failed" | "skipped";
};

export interface PaymentReminderProgressModalProps {
  isOpen: boolean;
  completed: number;
  total: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  issues: PaymentReminderIssue[];
  isComplete: boolean;
  onClose: () => void;
}

function StatPill({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "success" | "warning" | "danger" | "neutral";
}) {
  const toneClasses = {
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warning: "bg-amber-50 text-amber-900 border-amber-200",
    danger: "bg-red-50 text-red-800 border-red-200",
    neutral: "bg-gray-50 text-gray-font border-gray-200",
  }[tone];

  return (
    <div
      className={`rounded-md border px-3 py-2 text-sm ${toneClasses}`}
    >
      <span className="font-semibold tabular-nums">{value}</span>{" "}
      <span>{label}</span>
    </div>
  );
}

function resolveSummaryMessage(
  sentCount: number,
  failedCount: number,
  skippedCount: number,
  total: number,
): { message: string; tone: "success" | "warning" | "danger" } {
  const notSentCount = failedCount + skippedCount;

  if (sentCount === total && total > 0) {
    return {
      message: `All ${sentCount} reminder invoice${sentCount === 1 ? "" : "s"} were sent successfully.`,
      tone: "success",
    };
  }

  if (sentCount > 0 && notSentCount > 0) {
    return {
      message: `${sentCount} of ${total} sent. ${notSentCount} could not be sent.`,
      tone: "warning",
    };
  }

  if (sentCount === 0 && notSentCount > 0) {
    return {
      message: `None of the ${total} selected reminder invoice${total === 1 ? "" : "s"} could be sent.`,
      tone: "danger",
    };
  }

  return {
    message: "No invoices were sent.",
    tone: "danger",
  };
}

export default function PaymentReminderProgressModal({
  isOpen,
  completed,
  total,
  sentCount,
  failedCount,
  skippedCount,
  issues,
  isComplete,
  onClose,
}: PaymentReminderProgressModalProps) {
  const inProgress = isOpen && !isComplete;
  const processed = Math.min(completed, total);
  const progressPercent =
    total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const notSentCount = failedCount + skippedCount;
  const summary = resolveSummaryMessage(
    sentCount,
    failedCount,
    skippedCount,
    total,
  );

  const summaryToneClasses = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
    warning: "border-amber-200 bg-amber-50 text-amber-950",
    danger: "border-red-200 bg-red-50 text-red-900",
  }[summary.tone];

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={inProgress ? () => {} : onClose}
      title={
        isComplete
          ? notSentCount > 0
            ? "Reminder invoices finished with issues"
            : "Reminder invoices sent"
          : "Sending reminder invoices"
      }
      compact
      compactBodyScrolls={false}
      className="max-w-lg"
      bodyClassName="!pt-4"
    >
      <div className="flex flex-col gap-5 px-1">
        <div className="flex flex-col gap-3">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-3xl font-medium tabular-nums text-Default">
                {processed} / {total}
              </p>
              <p className="text-sm text-gray-font">
                {inProgress
                  ? "Processing selected registrations…"
                  : "Processing complete"}
              </p>
            </div>
            <p className="text-lg font-medium tabular-nums text-gray-font">
              {progressPercent}%
            </p>
          </div>

          <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-Default transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <StatPill label="sent" value={sentCount} tone="success" />
          <StatPill label="failed" value={failedCount} tone="danger" />
          <StatPill label="skipped" value={skippedCount} tone="warning" />
        </div>

        {isComplete ? (
          <div
            className={`rounded-md border px-4 py-3 text-sm ${summaryToneClasses}`}
          >
            {summary.message}
          </div>
        ) : null}

        {issues.length > 0 ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-gray-font">
              {inProgress
                ? "Could not send so far:"
                : "Could not send to:"}
            </p>
            <ul className="max-h-48 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-200">
              {issues.map((issue, index) => (
                <li
                  key={`${issue.name}-${index}`}
                  className="px-3 py-2 text-sm"
                >
                  <p className="font-medium text-gray-font">{issue.name}</p>
                  <p className="text-gray-500">
                    {issue.kind === "failed" ? "Failed" : "Skipped"}:{" "}
                    {issue.reason}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {isComplete ? (
          <div className="flex justify-end">
            <Button
              text="Close"
              color="secondary"
              variant="filled"
              size="fixed"
              onClick={onClose}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
